/**
 * Chamados de suporte (tasks) — peças compartilhadas pelo usuário (gestor/treinador) e pela administração.
 *  - AttachmentPicker: escolha de anexos com validação no navegador (a regra de verdade é a do servidor);
 *  - TicketBrowser: histórico com abas Pendentes/Solucionadas/Todas, busca (ID, assunto, solicitante) e paginação;
 *  - TicketView: um chamado com linha do tempo, anexos, resposta e Concluir/Reabrir.
 * Todo texto é renderizado como texto (React escapa); anexos só saem por download autenticado.
 */
import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { api, ApiError, download, upload } from "../api/client";
import type { HelpInfo, SupportAttachment, SupportTicket, SupportTicketDetail, SupportTicketPage, TicketStatus } from "../api/types";
import { formatBytes, formatDateTime } from "../lib/format";
import { useAsync } from "../lib/useAsync";
import { Link } from "../router/router";
import { IconPaperclip } from "./icons";
import { Badge, Button, EmptyState, ErrorState, Loading, useToast } from "./ui";
import { t, tj } from "../i18n";

const BODY_MAX = 2000;

export function useHelpInfo(): HelpInfo | null {
  return useAsync(() => api.get<HelpInfo>("/help"), []).data;
}

function asApiError(e: unknown): ApiError {
  return e instanceof ApiError ? e : new ApiError(0, "unknown", String(e));
}

export function TicketStatusBadge({ status, alert }: { status: TicketStatus; alert?: boolean }) {
  return status === "open" ? <Badge tone={alert ? "danger" : "warn"}>{t("Pendente")}</Badge> : <Badge tone="ok">{t("Solucionada")}</Badge>;
}

/* ------------------------------------------------------------------ anexos */

export function AttachmentPicker({
  files,
  onChange,
  info,
  disabled,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  info: HelpInfo | null;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const id = useId();
  const maxMb = info?.attachment_max_mb ?? 10;
  const maxFiles = info?.attachment_max_files ?? 3;
  const accept = info?.attachment_accept ?? "";
  const allowed = accept ? accept.split(",") : null;

  function add(list: FileList | null) {
    if (!list) return;
    const next = [...files];
    let msg: string | null = null;
    for (const f of Array.from(list)) {
      const ext = "." + (f.name.split(".").pop() ?? "").toLowerCase();
      if (next.length >= maxFiles) msg = t("No máximo {maxFiles} arquivos por mensagem.", { maxFiles });
      else if (allowed && !allowed.includes(ext)) msg = t("“{name}”: tipo de arquivo não permitido.", { name: f.name });
      else if (f.size === 0) msg = t("“{name}” está vazio.", { name: f.name });
      else if (f.size > maxMb * 1024 * 1024) msg = t("“{name}” tem {size}; o limite é {maxMb} MB por arquivo.", { name: f.name, size: formatBytes(f.size), maxMb });
      else next.push(f);
    }
    setProblem(msg);
    onChange(next);
    if (input.current) input.current.value = "";
  }

  return (
    <div className="attach">
      <input
        ref={input}
        id={id}
        type="file"
        className="visually-hidden"
        multiple
        accept={accept}
        disabled={disabled}
        onChange={(e) => add(e.target.files)}
        aria-describedby={`${id}-hint`}
      />
      <div className="attach-bar">
        <Button size="sm" onClick={() => input.current?.click()} disabled={disabled || files.length >= maxFiles}>
          <IconPaperclip width={16} height={16} /> {t("Anexar arquivo")}
        </Button>
        <span className="muted attach-hint" id={`${id}-hint`}>
          {" "}{t("Até {maxFiles} arquivos de {maxMb} MB (PDF, imagens, texto, planilhas e documentos do Office).", { maxFiles, maxMb })}{" "}</span>
      </div>
      {problem && (
        <p className="attach-problem" role="alert">
          {problem}
        </p>
      )}
      {files.length > 0 && (
        <ul className="attach-list" aria-label={t("Arquivos a enviar")}>
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`}>
              <IconPaperclip width={14} height={14} />
              <span className="attach-name">{f.name}</span>
              <span className="muted">{formatBytes(f.size)}</span>
              <button
                type="button"
                className="attach-remove"
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                aria-label={t("Remover {name}", { name: f.name })}
                disabled={disabled}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AttachmentLink({ apiBase, ticketId, a }: { apiBase: string; ticketId: string; a: SupportAttachment }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className="attach-chip"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await download(`${apiBase}/${ticketId}/attachments/${a.id}`, a.filename);
        } catch (e) {
          toast(asApiError(e).message, "error");
        } finally {
          setBusy(false);
        }
      }}
      title={t("Baixar {filename}", { filename: a.filename })}
    >
      <IconPaperclip width={14} height={14} />
      <span className="attach-name">{a.filename}</span>
      <span className="muted">{formatBytes(a.size_bytes)}</span>
    </button>
  );
}

/* ------------------------------------------------------------------ histórico (lista + filtro + busca) */

type Tab = "open" | "solved" | "all";
const TAB_LABEL: Record<Tab, string> = { open: t("Pendentes"), solved: t("Solucionadas"), all: t("Todas") };
const PAGE_SIZE = 10;

export function TicketBrowser({
  listPath,
  hrefFor,
  admin,
  emptyTitle,
  emptyHint,
  onData,
}: {
  listPath: string;
  hrefFor: (tp: SupportTicket) => string;
  admin?: boolean;
  emptyTitle: string;
  emptyHint?: string;
  onData?: (p: SupportTicketPage) => void;
}) {
  const [tab, setTab] = useState<Tab>("open");
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<SupportTicketPage | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const seq = useRef(0);
  const searchId = useId();

  useEffect(() => {
    const tp = window.setTimeout(() => {
      setQ(text.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(tp);
  }, [text]);

  const load = useCallback(async () => {
    const id = ++seq.current;
    try {
      const r = await api.get<SupportTicketPage>(listPath, {
        status: tab === "all" ? undefined : tab,
        q: q || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      if (id !== seq.current) return;
      setData(r);
      setError(null);
      onData?.(r);
    } catch (e) {
      if (id === seq.current) setError(asApiError(e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listPath, tab, q, page]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => document.visibilityState === "visible" && void load(), 30_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const counts: Record<Tab, number | null> = {
    open: data?.open_count ?? null,
    solved: data?.solved_count ?? null,
    all: data ? data.open_count + data.solved_count : null,
  };
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="tickets">
      <div className="tickets-tools">
        <div className="tabs" role="tablist" aria-label={t("Situação dos chamados")}>
          {(Object.keys(TAB_LABEL) as Tab[]).map((tp) => (
            <button
              key={tp}
              type="button"
              role="tab"
              aria-selected={tab === tp}
              className={`tab ${tab === tp ? "active" : ""}`}
              onClick={() => {
                setTab(tp);
                setPage(1);
              }}
            >
              {TAB_LABEL[tp]}
              {counts[tp] !== null && <span className="tab-count">{counts[tp]}</span>}
            </button>
          ))}
        </div>
        <div className="tickets-search">
          <label htmlFor={searchId} className="visually-hidden">
            {t("Buscar chamados")}
          </label>
          <input
            id={searchId}
            type="search"
            className="input"
            placeholder={admin ? t("Buscar por ID (SUP-000012), assunto ou solicitante") : t("Buscar por ID (SUP-000012) ou assunto")}
            value={text}
            maxLength={100}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
      </div>

      {error && !data ? (
        <ErrorState error={error} onRetry={() => void load()} />
      ) : !data ? (
        <Loading label={t("Carregando chamados…")} />
      ) : data.items.length === 0 ? (
        <EmptyState title={q ? t("Nenhum chamado encontrado para a busca.") : emptyTitle}>{q ? t("Confira o número (ex.: SUP-000012) ou tente outra palavra.") : emptyHint}</EmptyState>
      ) : (
        <>
          <ul className={`ticket-list ${admin ? "is-admin" : ""}`} aria-label={t("Chamados")}>
            {data.items.map((tp) => (
              <li key={tp.id}>
                <Link to={hrefFor(tp)} className={`ticket-card ${tp.status === "open" ? "is-open" : "is-solved"} ${tp.unread_count ? "has-unread" : ""}`}>
                  <span className="ticket-top">
                    <span className="ticket-code">{tp.code}</span>
                    <TicketStatusBadge status={tp.status} alert={admin} />
                    {tp.unread_count > 0 && (
                      <span className="nav-badge ticket-unread" aria-label={`${tp.unread_count} nova(s)`}>
                        {tp.unread_count} {tp.unread_count === 1 ? "nova" : "novas"}
                      </span>
                    )}
                    <time className="muted ticket-time" dateTime={tp.last_activity_at}>
                      {formatDateTime(tp.last_activity_at)}
                    </time>
                  </span>
                  <strong className="ticket-subject">{tp.subject}</strong>
                  {admin && (
                    <span className="muted ticket-who">
                      {tp.user_name}
                      {tp.organization_name ? ` · ${tp.organization_name}` : ""}
                    </span>
                  )}
                  <span className="ticket-preview">
                    {tp.last_from === (admin ? "admin" : "user") ? t("Você: ") : ""}
                    {tp.last_preview}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          {data.total > PAGE_SIZE && (
            <nav className="pagination" aria-label={t("Paginação dos chamados")}>
              <span>
                {t("{a}–{b} de {total}", { a: (page - 1) * PAGE_SIZE + 1, b: Math.min(data.total, page * PAGE_SIZE), total: data.total })}
              </span>
              <span style={{ display: "flex", gap: 6 }}>
                <Button size="sm" onClick={() => setPage(page - 1)} disabled={page <= 1}>
                  {t("Anterior")}
                </Button>
                <Button size="sm" onClick={() => setPage(page + 1)} disabled={page >= pages}>
                  {t("Próxima")}
                </Button>
              </span>
            </nav>
          )}
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ um chamado */

export function TicketView({
  apiBase,
  ticketId,
  admin,
  backTo,
  backLabel,
}: {
  apiBase: string;
  ticketId: string;
  admin?: boolean;
  backTo: string;
  backLabel: string;
}) {
  const toast = useToast();
  const info = useHelpInfo();
  const [ticket, setTicket] = useState<SupportTicketDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [changing, setChanging] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const last = useRef(0);
  const epoch = useRef(0);
  const areaId = useId();
  const path = `${apiBase}/${ticketId}`;
  const readEvent = admin ? "eapace:support-unread" : "eapace:notifications";

  const load = useCallback(async () => {
    const mine = epoch.current;
    try {
      const tp = await api.get<SupportTicketDetail>(path);
      if (mine !== epoch.current) return;
      setTicket(tp);
      setError(null);
      if (tp.unread_count > 0) {
        await api.post(`${path}/read`);
        window.dispatchEvent(new Event(readEvent));
      }
    } catch (e) {
      if (mine === epoch.current) setError(asApiError(e));
    }
  }, [path, readEvent]);

  useEffect(() => {
    epoch.current += 1;
    setTicket(null);
    setText("");
    setFiles([]);
    last.current = 0;
    void load();
    const timer = window.setInterval(() => document.visibilityState === "visible" && void load(), 30_000);
    return () => {
      window.clearInterval(timer);
      epoch.current += 1;
    };
  }, [load]);

  useEffect(() => {
    const n = ticket?.messages.length ?? 0;
    if (n > last.current && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    last.current = n;
  }, [ticket]);

  async function send(e?: FormEvent) {
    e?.preventDefault();
    const body = text.trim();
    if ((!body && files.length === 0) || sending) return;
    setSending(true);
    try {
      const form = new FormData();
      form.append("body", body);
      files.forEach((f) => form.append("files", f));
      setTicket(await upload<SupportTicketDetail>(`${path}/messages`, form));
      setText("");
      setFiles([]);
      window.dispatchEvent(new Event(readEvent));
    } catch (err) {
      toast(asApiError(err).message, "error");
    } finally {
      setSending(false);
    }
  }

  async function change(action: "solve" | "reopen") {
    setChanging(true);
    try {
      setTicket(await api.post<SupportTicketDetail>(`${path}/${action}`));
      window.dispatchEvent(new Event("eapace:support-unread"));
      toast(action === "solve" ? t("Task concluída.") : t("Task reaberta."), "ok");
    } catch (err) {
      toast(asApiError(err).message, "error");
    } finally {
      setChanging(false);
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) void send();
  }

  if (error && !ticket) return <ErrorState error={error} onRetry={() => void load()} />;
  if (!ticket) return <Loading label={t("Carregando chamado…")} />;
  const open = ticket.status === "open";

  return (
    <article className={`ticket-view ${open && admin ? "is-alert" : ""}`}>
      <Link to={backTo} className="ticket-back">
        ← {backLabel}
      </Link>
      <header className="ticket-head">
        <div className="ticket-head-main">
          <div className="ticket-top">
            <span className="ticket-code">{ticket.code}</span>
            <TicketStatusBadge status={ticket.status} alert={admin} />
          </div>
          <h1 className="ticket-title">{ticket.subject}</h1>
          <p className="muted ticket-meta">
            {t("Aberto em {date}", { date: formatDateTime(ticket.created_at) })}
            {admin && (
              <>
                {" "}
                {tj("por {name} ({email})", { name: <strong>{ticket.user_name}</strong>, email: ticket.user_email })}
                {ticket.organization_name ? ` · ${ticket.organization_name}` : ""}
              </>
            )}
            {!open && ticket.solved_at && (
              <>
                {" "}
                {ticket.solved_by_name
                  ? t("· Concluída em {date} por {name}", { date: formatDateTime(ticket.solved_at), name: ticket.solved_by_name })
                  : t("· Concluída em {date}", { date: formatDateTime(ticket.solved_at) })}
              </>
            )}
          </p>
        </div>
        <div className="ticket-actions">
          {open ? (
            <Button variant="primary" busy={changing} onClick={() => void change("solve")}>
              {t("Concluir task")}
            </Button>
          ) : (
            <Button busy={changing} onClick={() => void change("reopen")}>
              {t("Reabrir task")}
            </Button>
          )}
        </div>
      </header>

      {ticket.has_more && <p className="muted chat-more">{t("Mostrando as 300 mensagens mais recentes.")}</p>}
      <ul className="ticket-timeline" ref={listRef} aria-label={t("Histórico do chamado")} tabIndex={0}>
        {ticket.messages.map((m) =>
          m.author_kind === "system" ? (
            <li key={m.id} className="ticket-event">
              <span>{m.body}</span>
              <time dateTime={m.created_at}>{formatDateTime(m.created_at)}</time>
            </li>
          ) : (
            <li key={m.id} className={`chat-msg ${m.mine ? "mine" : "theirs"} ${m.author_kind === "admin" ? "staff" : ""}`}>
              <div className="chat-meta">
                <strong>{m.mine ? t("Você") : (m.author_name ?? t("Suporte EAPace"))}</strong>
                {m.author_kind === "admin" && <span className="chat-role">{t("Suporte")}</span>}
                <time dateTime={m.created_at}>{formatDateTime(m.created_at)}</time>
              </div>
              {m.body && <p className="chat-body">{m.body}</p>}
              {m.attachments.length > 0 && (
                <div className="attach-chips">
                  {m.attachments.map((a) => (
                    <AttachmentLink key={a.id} apiBase={apiBase} ticketId={ticket.id} a={a} />
                  ))}
                </div>
              )}
            </li>
          ),
        )}
      </ul>

      {ticket.can_reply ? (
        <form className="chat-form" onSubmit={send}>
          <label htmlFor={areaId} className="chat-label">
            {admin ? t("Responder a {user_name}", { user_name: ticket.user_name }) : t("Escreva para o suporte EAPace")}
          </label>
          <textarea
            id={areaId}
            className="textarea"
            rows={3}
            maxLength={BODY_MAX}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            placeholder={admin ? t("Escreva a resposta…") : t("Acrescente informações ou responda ao suporte…")}
          />
          <AttachmentPicker files={files} onChange={setFiles} info={info} disabled={sending} />
          <div className="chat-actions">
            <span className="muted chat-hint">
              {admin ? t("O usuário recebe um aviso no sino. ") : t("Não envie dados de saúde ou pessoais de atletas. ")}
              {t("Ctrl+Enter envia · {n}/{max}", { n: text.length, max: BODY_MAX })}
            </span>
            <Button type="submit" variant="primary" busy={sending} disabled={!text.trim() && files.length === 0}>
              {admin ? t("Responder") : t("Enviar")}
            </Button>
          </div>
        </form>
      ) : (
        <p className="ticket-closed">
          {t("Esta task está concluída. Para enviar uma nova mensagem, reabra a task.")}
        </p>
      )}
    </article>
  );
}
